/**
 * One anchored comment card (#2244).
 *
 * Three inline modes — no modal anywhere. Compose is expressed as
 * `initialMode='edit'` with an empty `initialText`.
 *
 * - view    — name, timestamp, body; edit / delete / resolve buttons
 *             (`comment-card-view.tsx`).
 * - edit    — auto-growing textarea, character counter, Save / Cancel,
 *             inline save-error line (`comment-card-edit.tsx`).
 * - confirm — inline "Delete this comment?" prompt
 *             (`comment-card-confirm-delete.tsx`).
 *
 * This file dispatches between the three, owns the local mode + text
 * state, and reports height changes back to the gutter. Header +
 * mode bodies live in sibling files so no file needs
 * `eslint-disable max-lines`.
 */

import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { CommentCardHeader } from './comment-card-header.tsx';
import { CommentCardView } from './comment-card-view.tsx';
import { CommentCardEdit } from './comment-card-edit.tsx';
import { CommentCardConfirmDelete } from './comment-card-confirm-delete.tsx';

export { COMMENT_MAX_LENGTH } from './comment-card-edit.tsx';

type Mode = 'view' | 'edit' | 'confirm-delete';

export interface CommentCardProps {
  id: string;
  authorName: string | undefined;
  createdAt: string | undefined;
  initialText: string;
  initialMode?: Mode;
  /** Highlight the card (matches the paragraph selection etc.). */
  highlighted?: boolean;
  saving?: boolean;
  /** Called when the user enters edit mode from view. */
  onEnterEdit?: () => void;
  /**
   * Save the current draft. Return a promise; a rejection surfaces
   * inline via `CommentCardEdit`'s save-error state — the text stays.
   */
  onSave: (nextText: string) => Promise<void>;
  /** Delete the comment after the in-card confirmation. */
  onDelete?: () => Promise<void> | void;
  /**
   * Compose flow only. Called when the user cancels an empty new
   * comment (Escape or Cancel). Existing comments return to view.
   */
  onCancel?: () => void;
  /** Reports the card's rendered height for the gutter layout. */
  onHeightChange?: (heightPx: number) => void;
  /** Called when the user clicks/focuses the card body. */
  onFocus?: () => void;
  /** #2246 — true once the comment is marked resolved. */
  resolved?: boolean;
  /** Toggle resolve/unresolve. Undefined disables the button. */
  onToggleResolved?: () => Promise<void> | void;
  /** Hide edit/delete/resolve; used for source comments in translation view. */
  readOnly?: boolean;
  /** Optional pill next to the author — e.g. "Source". */
  ownershipLabel?: string;
}

export function CommentCard({
  id,
  authorName,
  createdAt,
  initialText,
  initialMode = 'view',
  highlighted = false,
  saving = false,
  onEnterEdit,
  onSave,
  onDelete,
  onCancel,
  onHeightChange,
  onFocus,
  resolved = false,
  onToggleResolved,
  readOnly = false,
  ownershipLabel
}: CommentCardProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [mode, setMode] = useState<Mode>(initialMode);
  const rootRef = useRef<HTMLElement | null>(null);

  // Stabilise the height-reporter across renders. The gutter passes
  // a brand-new arrow function every render (`h => handleHeightChange(
  // key, h)`), so declaring the callback as a dependency of the layout
  // effect below would re-mount the ResizeObserver on every render —
  // and the observer's initial fire would restart the loop. Reading
  // the latest callback through a ref keeps the effect's identity
  // stable while still calling the newest handler.
  const onHeightChangeRef = useRef(onHeightChange);
  useEffect(() => {
    onHeightChangeRef.current = onHeightChange;
  }, [onHeightChange]);

  // Report height changes to the parent so it can restack cards. Uses
  // border-box height (`getBoundingClientRect().height`) so padding +
  // border are included — that is the visual space the next card
  // must clear. Sub-pixel values are rounded so the parent's
  // `prev === next` setState bail-out actually engages: a jittering
  // 91.0001 vs 91.0002 measurement would otherwise re-trigger every
  // render, tearing down and rebuilding the observer, and eventually
  // tripping React's "Maximum update depth exceeded" guard.
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const report = (node: Element) => {
      const cb = onHeightChangeRef.current;
      if (!cb) return;
      cb(Math.round(node.getBoundingClientRect().height));
    };
    report(el);
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) report(entry.target);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [mode]);

  const enterEdit = useCallback(() => {
    setMode('edit');
    onEnterEdit?.();
  }, [onEnterEdit]);

  const cancelEdit = useCallback(() => {
    if (initialMode === 'edit') onCancel?.();
    else setMode('view');
  }, [initialMode, onCancel]);

  const handleSave = useCallback(
    async (next: string) => {
      await onSave(next);
      if (initialMode !== 'edit') setMode('view');
    },
    [initialMode, onSave]
  );

  const highlightStyle: Record<string, string> = highlighted
    ? { ['--tw-ring-color' as never]: getColour('activeText') }
    : {};

  const body: ReactNode = (() => {
    if (mode === 'edit') {
      return (
        <CommentCardEdit
          id={id}
          initialText={initialText}
          saving={saving}
          onSave={handleSave}
          onCancel={cancelEdit}
        />
      );
    }
    if (mode === 'confirm-delete') {
      return (
        <CommentCardConfirmDelete
          id={id}
          onCancel={() => setMode('view')}
          onConfirm={() => onDelete?.()}
        />
      );
    }
    return (
      <CommentCardView
        text={initialText}
        resolved={resolved}
        readOnly={readOnly}
        onEnterEdit={enterEdit}
        onConfirmDelete={() => setMode('confirm-delete')}
        onToggleResolved={onToggleResolved}
        hasDelete={!!onDelete}
      />
    );
  })();

  return (
    <article
      ref={el => (rootRef.current = el)}
      onClick={onFocus}
      aria-label={authorName ? `${t('comment')} — ${authorName}` : t('comment')}
      className={cn(
        'px-3 pt-2 transition-colors',
        highlighted && 'ring-1',
        resolved && 'opacity-70'
      )}
      style={{
        // Let the gutter wrapper's white background show through — the
        // card only adds a soft highlight overlay on focus. Keeps the
        // popup theme-independent (user asked for white).
        backgroundColor: highlighted ? 'rgba(59,130,246,0.08)' : 'transparent',
        ...highlightStyle
      }}
    >
      <CommentCardHeader
        authorName={authorName}
        createdAt={createdAt}
        ownershipLabel={ownershipLabel}
        resolved={resolved}
      />
      {body}
    </article>
  );
}
